import { z } from 'zod';
import { assertNever } from '../lib/assert-never.js';
import { rangementTargetsKept } from '../protocol/rangement.js';
import {
  ANALYST_PAIR_THRESHOLD_DEFAULT,
  ANALYST_READING_MAX,
  ANALYST_SOLO_THRESHOLD_DEFAULT,
  ANALYST_STYLE_RUNS_DEFAULT,
  ANALYST_STYLE_WEIGHT_DEFAULT,
  analystLevelSchema,
  analystStyleRunsSchema,
  analystStyleWeightSchema,
  analystThresholdSchema,
} from './analyst.js';
import {
  VERSIONED_DEFAULT_SORT,
  renameRequestSchemaFor,
  saveDraftRequestSchema,
  saveVersionRequestSchema,
  versionPinSchema,
  versionedCardShape,
  versionedContentSchema,
  versionedDetailSchema,
  versionedFiltersShape,
  versionedRefSchema,
  versionedSortSchema,
} from './versioning.js';

/**
 * A protocol: the ordered SUITE OF STEPS an experience plays on its protocol
 * page (refonte modulaire §7, §8.4, §8.6; grill of 2026-09-09 and 2026-09-10,
 * issue #47).
 *
 * Third consumer of the common versioning pattern (§4), and the first one that
 * CITES another object: every objective names the criteria that validate it,
 * as `{ id, pin }` references, and a direction may quote a criterion through a
 * `{{critere:id}}` variable (§8.6). Everything generic - the versions, the two
 * pointers, the draft, the retirement, « utilisé dans » - comes from
 * `versioning.ts`; what is here is only what depends on what a protocol MEANS.
 *
 * ## The stored schema is PERMISSIVE, and the refusals are elsewhere
 *
 * §7.1 says what « Enregistrer une nouvelle version » refuses: a step without a
 * turn cap, an objective without a criterion, a jump or a fallback aimed at a
 * step that no longer exists. None of those is a shape a Zod object can
 * refuse: the cap is a `number | null` because a DRAFT has to hold a step whose
 * cap is not typed yet, an objective's criteria list is empty while somebody
 * adds the first one, and a jump's target is a cross-reference inside the same
 * content. So the schema accepts them all, and ONE pure rule set -
 * `protocolProblems` in `../protocol/problems.ts` - names each of them, for the
 * server that refuses the version and for the editor that greys the box.
 *
 * What the schema DOES hold is the shape §8.4 fixes for a condition: triggers
 * in « et » groups separated by « ou », and actions with the non-terminal ones
 * first and at most one terminal one, last. The editor normalises that order
 * on every gesture, so a stored row can only violate it if a client wrote it
 * by hand - which is exactly what a wire-level refinement is for.
 *
 * ## Identifiers are English, labels are French
 *
 * `close-experience`, `go-to-step`, `attempts-exhausted`: a stored enum is
 * code, and the French sentence beside each of them lives in the frontend's
 * `protocol-labels.ts`. The one French thing stored is the closing text the
 * person reads, and that is the team's words, not an identifier.
 *
 * ## Step, objective and condition ids are the CLIENT's
 *
 * A jump aims at a step's `id` and never at its rank (§7.1: « un déplacement
 * ne casse rien »), a trigger names an objective by its `id`, and a variable
 * stores `{{objectif:id}}` so a rename invalidates nothing (§8.6). The editor
 * strikes them (a uuid) when it adds a row; the server checks they are unique
 * inside one content and nothing more.
 */

/* --------------------------------------------------------------- the bounds */

/**
 * H21 (§16). Bounds of INPUT, not of meaning: an operator meets them by
 * mistake, never on purpose. The name and the direction sit where the
 * criterion's do (H10); the short description is « en une phrase » (§7.1).
 */
export const PROTOCOL_NAME_MAX = 120;
export const PROTOCOL_DESCRIPTION_MAX = 300;
export const PROTOCOL_STEPS_MAX = 50;
export const PROTOCOL_STEP_NAME_MAX = 80;
export const PROTOCOL_DIRECTION_MAX = 4000;
export const PROTOCOL_OBJECTIVES_MAX = 20;
export const PROTOCOL_OBJECTIVE_NAME_MAX = 80;
export const PROTOCOL_OBJECTIVE_CRITERIA_MAX = 10;
/** The longest label of an « l'un de » group (issue #181): an identifier, never shown as text. */
export const PROTOCOL_OR_GROUP_MAX = 40;
export const PROTOCOL_CONDITIONS_MAX = 20;
export const PROTOCOL_CONDITION_GROUPS_MAX = 10;
export const PROTOCOL_GROUP_TRIGGERS_MAX = 10;
export const PROTOCOL_CONDITION_ACTIONS_MAX = 10;
export const PROTOCOL_CLOSE_TEXT_MAX = 600;
export const PROTOCOL_ACTION_DIRECTION_MAX = 2000;
/** The « consigne du rangeur » (scenario-succes.md §4, H7): 6000 characters at most. */
export const PROTOCOL_RANGEMENT_CONSIGNE_MAX = 6000;

/** The closing text every « fermer l'expérience » is prefilled with (§7.2). */
export const PROTOCOL_DEFAULT_CLOSE_TEXT = 'Je suis désolé, je n’ai pas pu donner suite.';

/*
 * H18 (« 50 par défaut ») had its constant here until the second MR of issue
 * #81, and it is RETIRED (§8.2, §16): a criterion cited without a threshold is
 * filled from the plancher of its own notation, and one without a notation is
 * filled on a verdict. The engine no longer has a number to fall back on, and
 * the two old thresholds H46 cannot convert (`<=` and `<`) read as « sans
 * seuil », which now means « the criterion's own plancher » rather than 50.
 *
 * `PROTOCOL_PROPOSED_THRESHOLD` went with it: the slider of §7.2 opens on the
 * band the plancher sits on, which is the criterion's own number and not a
 * constant of the protocol.
 */

/**
 * The id of a step, an objective or a condition: struck by the editor, stable
 * across reorders and renames. Bounded because it is stored inside a version.
 */
export const protocolLocalIdSchema = z.string().min(1).max(64);

/* ------------------------------------------------------------ the objective */

/**
 * The shape the grill of 2026-09-09 stored a threshold in: one of four
 * operators and a value. Kept PRIVATE and never exported - nothing may write it
 * again, and the only thing left to do with it is read it.
 */
const legacyThresholdSchema = z.object({
  op: z.enum(['>=', '>', '<=', '<']),
  value: z.number().int().min(0).max(100),
});

/**
 * H46 (§16): what one ALREADY STORED threshold reads as.
 *
 * `>=` keeps its value. `>` becomes the first score that passes it, capped at
 * 100 - `> 100` has no minimum that satisfies it, and 100 is the strictest one
 * that exists. The `+ 1` leans on a score being a whole number
 * (`snapshotCriterionSchema`), which is what makes « strictly above 80 » and
 * « at least 81 » the same rule.
 *
 * `<=` and `<` have NO equivalent: a threshold read « la note est basse » is a
 * notation written backwards, not an objective's need (§7.2). H46 rules both
 * « sans seuil », and the consequence is named rather than hidden: « sans
 * seuil » means the criterion's own plancher (§8.2), so an old `< 30` does not
 * disappear, it INVERTS into « at least the plancher ». The grill accepted
 * that: the four operators only ever existed between the grill of 2026-09-09
 * and this one, and a threshold posed downwards was already a protocol nobody
 * could play as intended. A frozen snapshot is not re-judged either - it keeps
 * the `satisfied` its turn was decided on (§8.7), so a reader of §12.3 can show
 * a ticked criterion beside a bar today's rule would not have passed.
 */
function readLegacyThreshold(legacy: z.infer<typeof legacyThresholdSchema>): number | null {
  switch (legacy.op) {
    case '>=':
      return legacy.value;
    case '>':
      return Math.min(legacy.value + 1, 100);
    case '<=':
      return null;
    case '<':
      return null;
    default:
      return assertNever(legacy.op, 'stored threshold operator');
  }
}

/**
 * The threshold an objective poses on one criterion's score (§7.2): a MINIMUM,
 * read « note >= N », on the 0 to 100 scale a score lives on - so the mockup
 * writes « seuil 75 » and no unit, a percent sign having nothing to be a
 * percentage OF.
 *
 * One number and no operator since the grill of 2026-09-16 (issue #79): a
 * notation is monotone by construction, higher is better, so the only
 * comparison an objective has a use for is a floor. It is stored as the NUMBER
 * and never as the index of a tranche, so that a later version of the notation
 * moving its tranches cannot silently move a threshold somebody chose.
 *
 * NOT nullable: « no threshold at all » is a fact about the CITATION, not a
 * kind of threshold, so each use site spells its own `.nullable()`.
 */
export const criterionThresholdSchema = z.number().int().min(0).max(100);
export type CriterionThreshold = z.infer<typeof criterionThresholdSchema>;

/**
 * The same threshold as a STORED row may hold it (H46): the minimum, « sans
 * seuil », or the `{ op, value }` the grill of 2026-09-09 wrote, converted.
 *
 * A union with a transform rather than a `z.preprocess`: preprocess types its
 * input `unknown`, which would erase the threshold from the `z.input` of every
 * shape that nests it - and this repo has already been bitten by a stored shape
 * whose regression no suite could see.
 *
 * Reading and never rewriting is the whole of H46: « les seuils déjà stockés se
 * lisent au chargement sans réécrire les versions », §4.1 making a version
 * immutable. Which is also why this leniency is for READS ONLY - the request
 * bodies below refuse the old object outright, so no client can post one today
 * and leave a second generation of rows to convert.
 */
export const storedCriterionThresholdSchema = z.union([
  criterionThresholdSchema,
  z.null(),
  legacyThresholdSchema.transform(readLegacyThreshold),
]);

/**
 * Is `read` exactly what H46 reads `stored` as - `stored` being a threshold
 * written in the shape of the grill of 2026-09-09, `{ op, value }`?
 *
 * For the locks that compare a stored content to what its reader gives back
 * (`firstLoss`, vitrine.md §4.3 and §9.2): an old object that became a number,
 * or `null`, is a LOSS to them, while here it is the very reading H46 wants,
 * the same one the code before this one made. Answering it takes the private
 * schema, which is why the question is asked HERE and the schema stays private:
 * this function reads, it cannot be used to write the old object again, and no
 * request body accepts it.
 *
 * `null` is a reading like any other: `<=` and `<` read `null`, and `null` is
 * only accepted for those.
 */
export function isLegacyThresholdReading(stored: unknown, read: unknown): boolean {
  const legacy = legacyThresholdSchema.safeParse(stored);
  return legacy.success && read === readLegacyThreshold(legacy.data);
}

/**
 * One criterion of an objective's « condition de validation » (§7.2): the
 * reference (`latest` by default, a precise version when pinned, vitrine.md
 * §3.1) and its optional threshold. All the criteria of an objective are REQUIRED together.
 */
export const criterionRequirementSchema = z.object({
  ref: versionedRefSchema,
  threshold: storedCriterionThresholdSchema.default(null),
  /**
   * The « l'un de » group this citation belongs to (issue #181, scenario-succes-
   * conduite.md §4.1): neighbouring citations carrying the same value form ONE
   * group, filled as soon as one of them is. Absent, the citation is a group of
   * its own - which is every protocol written before the field existed, read
   * exactly as before. `objectiveCriterionGroups` is the one place the groups
   * are cut; `protocolProblems` refuses a group of a single citation.
   */
  orGroup: z.string().trim().min(1).max(PROTOCOL_OR_GROUP_MAX).optional(),
});
export type CriterionRequirement = z.infer<typeof criterionRequirementSchema>;

/**
 * The « quantité de matière attendue » (§7.2): N sentences at least, N
 * characters at least, each optional. Counted in CODE on the extracts the
 * scribe attributes, never estimated by the model (§8.2).
 */
export const objectiveQuantitySchema = z.object({
  minSentences: z.number().int().positive().nullable().default(null),
  minChars: z.number().int().positive().nullable().default(null),
});
export type ObjectiveQuantity = z.infer<typeof objectiveQuantitySchema>;

export const EMPTY_OBJECTIVE_QUANTITY: ObjectiveQuantity = { minSentences: null, minChars: null };

/**
 * An objective (§7.2), as a VERSION stores it.
 *
 * `mandatory` is the « niveau d'obligation » pill: obligatoire or optionnel,
 * nothing in between (§7.3 makes the two behave differently in the implicit
 * logic). The completion status (`vide`, `réussi`, `raté`) is a SESSION fact
 * and is not stored here.
 *
 * `analystLevel` (analyste.md §3.1, K1; issue #196) is the LEVEL the analyst of
 * the noyau reads this objective at: `why`, `how`, `what` or `null`, the
 * default - an objective with no level is not read by the first pass (the
 * subject, the example, the small talk). It is a datum of the OBJECTIVE, so it
 * follows its renames, its copy and its deletion; it is set in the Analyste
 * section of the editor and nowhere else (G4), and the first pass is never
 * shown it (G10): only the code reads it, to fix and to eliminate. `.default(null)`
 * so every version written before #196 reads without a level, with no migration.
 *
 * NOT `.strict()`, like every stored shape (§4.1): a field added later must not
 * make today's rows unreadable.
 */
export const protocolObjectiveSchema = z.object({
  id: protocolLocalIdSchema,
  name: z.string().trim().min(1).max(PROTOCOL_OBJECTIVE_NAME_MAX),
  mandatory: z.boolean(),
  /** For the interviewer. May carry the variables of §8.6. Empty is allowed. */
  direction: z.string().trim().max(PROTOCOL_DIRECTION_MAX).default(''),
  criteria: z.array(criterionRequirementSchema).max(PROTOCOL_OBJECTIVE_CRITERIA_MAX).default([]),
  quantity: objectiveQuantitySchema.default(EMPTY_OBJECTIVE_QUANTITY),
  analystLevel: analystLevelSchema.nullable().default(null),
});
export type ProtocolObjective = z.infer<typeof protocolObjectiveSchema>;

/* ---------------------------------------------------------------- the rules */

/**
 * What happens beyond the attempts (§7.2): close the experience with a closing
 * text of this step's own, or go to another step.
 */
export const stepFallbackSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('close-experience'),
    text: z.string().trim().min(1).max(PROTOCOL_CLOSE_TEXT_MAX),
  }),
  z.object({ kind: z.literal('go-to-step'), stepId: protocolLocalIdSchema }),
]);
export type StepFallback = z.infer<typeof stepFallbackSchema>;

export const DEFAULT_STEP_FALLBACK: StepFallback = {
  kind: 'close-experience',
  text: PROTOCOL_DEFAULT_CLOSE_TEXT,
};

/**
 * The « règles générales » of a step (§7.2): the numbers the engine applies
 * without a condition being written (§8.3).
 *
 * `maxTurns` is REQUIRED for a version and NULLABLE here, and the two are not
 * a contradiction: a draft holds a step whose cap is not typed yet, and
 * `protocolProblems` is what refuses the version (§7.1). `attempts` counts the
 * passes of the step in ALL - 1 means no replay; « rejeux » is not a word of
 * the team.
 */
export const stepRulesSchema = z.object({
  maxTurns: z.number().int().positive().nullable().default(null),
  emptyAnswersAllowed: z.number().int().nonnegative().default(1),
  attempts: z.number().int().positive().default(1),
  fallback: stepFallbackSchema.default(DEFAULT_STEP_FALLBACK),
});
export type StepRules = z.infer<typeof stepRulesSchema>;

export const DEFAULT_STEP_RULES: StepRules = {
  maxTurns: null,
  emptyAnswersAllowed: 1,
  attempts: 1,
  fallback: DEFAULT_STEP_FALLBACK,
};

/* ------------------------------------------------------ the exit conditions */

/** The three statuses of an objective in session (§7.3). */
export const objectiveStatusSchema = z.enum(['empty', 'succeeded', 'failed']);
export type ObjectiveStatus = z.infer<typeof objectiveStatusSchema>;

/** The two statuses a condition can listen for: `vide` is never a destination a trigger names. */
export const objectiveOutcomeSchema = z.enum(['succeeded', 'failed']);
export type ObjectiveOutcome = z.infer<typeof objectiveOutcomeSchema>;

/** The four triggers on a TYPE of objective (§8.4). */
export const objectiveClassTriggerSchema = z.enum([
  'all-mandatory-succeeded',
  'all-optional-succeeded',
  'optional-failed',
  'mandatory-failed',
]);
export type ObjectiveClassTrigger = z.infer<typeof objectiveClassTriggerSchema>;

/**
 * One trigger of an exit condition (§8.4): a named objective changing status,
 * a type of objective, or one of the three counters.
 */
export const exitTriggerSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('objective-status'),
    objectiveId: protocolLocalIdSchema,
    to: objectiveOutcomeSchema,
  }),
  z.object({ kind: z.literal('objective-class'), which: objectiveClassTriggerSchema }),
  z.object({ kind: z.literal('turn-cap-reached') }),
  z.object({ kind: z.literal('attempts-exhausted') }),
  z.object({ kind: z.literal('empty-answers-exceeded') }),
]);
export type ExitTrigger = z.infer<typeof exitTriggerSchema>;

/**
 * One action of an exit condition (§8.4), out of the closed list of seven.
 *
 * `direction` and `invalidate-objective` are the two NON-TERMINAL ones: the
 * step goes on. The five others end the step, the protocol or the experience,
 * and a condition carries at most one of them, last.
 */
export const exitActionSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('direction'),
    /** A one-off consigne for the interviewer's next turn. May carry variables. */
    text: z.string().trim().max(PROTOCOL_ACTION_DIRECTION_MAX).default(''),
  }),
  z.object({ kind: z.literal('invalidate-objective'), objectiveId: protocolLocalIdSchema }),
  z.object({ kind: z.literal('close-step') }),
  z.object({ kind: z.literal('replay-step') }),
  z.object({ kind: z.literal('go-to-step'), stepId: protocolLocalIdSchema }),
  z.object({ kind: z.literal('close-protocol') }),
  z.object({
    kind: z.literal('close-experience'),
    text: z.string().trim().min(1).max(PROTOCOL_CLOSE_TEXT_MAX),
  }),
]);
export type ExitAction = z.infer<typeof exitActionSchema>;
export type ExitActionKind = ExitAction['kind'];

/** The five actions that END something, and therefore come last and alone (§8.4). */
export const TERMINAL_ACTION_KINDS: readonly ExitActionKind[] = [
  'close-step',
  'replay-step',
  'go-to-step',
  'close-protocol',
  'close-experience',
];

export function isTerminalAction(action: { kind: ExitActionKind }): boolean {
  return TERMINAL_ACTION_KINDS.includes(action.kind);
}

/**
 * The shape of the actions list §8.4 fixes: non-terminal first, at most one
 * terminal, last. Exported so the editor can normalise with the same rule the
 * server refuses on.
 */
export function actionsWellOrdered(actions: readonly { kind: ExitActionKind }[]): boolean {
  const terminals = actions.filter(isTerminalAction);
  if (terminals.length > 1) return false;
  if (terminals.length === 0) return true;
  return isTerminalAction(actions[actions.length - 1] ?? { kind: 'direction' });
}

/**
 * One exit condition (§8.4): triggers in « et » groups separated by « ou »,
 * and an ordered list of actions. The condition is true as soon as one group
 * is entirely true; the first true condition of a step wins.
 */
export const exitConditionSchema = z
  .object({
    id: protocolLocalIdSchema,
    groups: z
      .array(z.array(exitTriggerSchema).min(1).max(PROTOCOL_GROUP_TRIGGERS_MAX))
      .min(1)
      .max(PROTOCOL_CONDITION_GROUPS_MAX),
    actions: z.array(exitActionSchema).max(PROTOCOL_CONDITION_ACTIONS_MAX).default([]),
  })
  .superRefine((condition, ctx) => {
    if (!actionsWellOrdered(condition.actions)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['actions'],
        message: 'At most one terminal action, and it comes last.',
      });
    }
  });
export type ExitCondition = z.infer<typeof exitConditionSchema>;

/* ----------------------------------------------------------------- the step */

/**
 * A step (§7.2): a name, the interviewer's direction, its objectives, its
 * general rules and its exit conditions.
 *
 * `direction` may be EMPTY: the mockup's « étape vide » is a step with no
 * direction, no objective and no condition, and it is a legal version once its
 * cap is posed - the implicit logic closes it at the first turn (§8.3).
 *
 * `farewell` (issue #163, §7.2, H76) makes it an ÉTAPE D'ADIEU: entering it,
 * the interviewer writes ONE message, the goodbye, and the protocol closes
 * without waiting for an answer (§8.1, §8.3). A boolean and not a union
 * discriminated by kind, so a farewell step keeps the same fields - its
 * `objectives` and `conditions` EMPTY (`farewell-step-not-empty` refuses
 * otherwise, H77), its `rules` at their defaults and ignored by the engine.
 * `.default(false)`: every content stored before #163 reads as it played,
 * with no migration of the stored versions.
 *
 * `rereadOnEntry` (issue #165, §7.2, H93) is the case « À l'entrée, relire
 * l'étape d'où l'on vient »: entering the step, and before its first message,
 * the scribe reads ONCE the last pass of the step just left against this
 * step's objectives, and what passes is succeeded from the start (§8.1, §8.3).
 * Closed on a farewell step, which has no objective to fill
 * (`reread-on-farewell-step` refuses it). `.default(false)` for the same reason
 * as `farewell`: no stored protocol changes until someone ticks it.
 *
 * `hideObjectives` (issue #191, `scenario-succes-conduite.md` §4.3, H18) is the
 * case « Cacher les objectifs à l'interviewer »: on a ticked step the
 * interviewer is served the step's direction ONLY - neither the block of open
 * objectives nor the engine's « Il manque encore » directive. The scribe, the
 * statuses, the conditions and the caps do not change: only what the
 * interviewer reads does. Closed on a farewell step, which has no objective to
 * hide (`hide-objectives-on-farewell-step` refuses it, H19). `.default(false)`
 * for the same reason as the two above.
 */
export const protocolStepSchema = z.object({
  id: protocolLocalIdSchema,
  name: z.string().trim().min(1).max(PROTOCOL_STEP_NAME_MAX),
  farewell: z.boolean().default(false),
  rereadOnEntry: z.boolean().default(false),
  hideObjectives: z.boolean().default(false),
  direction: z.string().trim().max(PROTOCOL_DIRECTION_MAX).default(''),
  objectives: z.array(protocolObjectiveSchema).max(PROTOCOL_OBJECTIVES_MAX).default([]),
  rules: stepRulesSchema.default(DEFAULT_STEP_RULES),
  conditions: z.array(exitConditionSchema).max(PROTOCOL_CONDITIONS_MAX).default([]),
});
export type ProtocolStep = z.infer<typeof protocolStepSchema>;

/* -------------------------------------------------------------- the content */

/**
 * The ids a content must keep unique, and where a duplicate would bite: a
 * jump aims at a step id, a trigger and an invalidation at an objective id,
 * and a duplicate would make the target ambiguous for the engine and for the
 * frise alike.
 */
function refuseDuplicateIds(
  content: {
    steps: readonly {
      id: string;
      objectives: readonly { id: string }[];
      conditions: readonly { id: string }[];
    }[];
  },
  ctx: z.RefinementCtx,
): void {
  const seenSteps = new Set<string>();
  const seenObjectives = new Set<string>();
  content.steps.forEach((step, stepIndex) => {
    if (seenSteps.has(step.id)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['steps', stepIndex, 'id'],
        message: 'Step ids are unique inside a protocol.',
      });
    }
    seenSteps.add(step.id);
    step.objectives.forEach((objective, objectiveIndex) => {
      if (seenObjectives.has(objective.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['steps', stepIndex, 'objectives', objectiveIndex, 'id'],
          message: 'Objective ids are unique inside a protocol.',
        });
      }
      seenObjectives.add(objective.id);
    });
    const seenConditions = new Set<string>();
    step.conditions.forEach((condition, conditionIndex) => {
      if (seenConditions.has(condition.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['steps', stepIndex, 'conditions', conditionIndex, 'id'],
          message: 'Condition ids are unique inside a step.',
        });
      }
      seenConditions.add(condition.id);
    });
  });
}

/**
 * The « Rangement » section of a protocol version (scenario-succes.md §4,
 * issue #177): which objectives the RANGEUR files the whole conversation onto
 * (`targetObjectiveIds`, in the order of the protocol) and the free text that
 * tells it how (`consigne`), whose heading, targets, conversation and output
 * contract stay in code (`rangerSystemParts`).
 *
 * Empty targets mean no rangement. The two refusals (`rangement-consigne-missing`,
 * `rangement-targets-missing`) are `rangementProblems`; a target the steps no
 * longer carry leaves the list at the save, without a refusal
 * (`rangementTargetsKept`).
 *
 * `.default()` at both levels and no migration, the régime of `analystReading`:
 * a version stored before #177 reads with no rangement at all.
 *
 * `oneObjectivePerPassage` (relecture.md §12, issue #187) is the setting « Un
 * passage ne sert qu'un objectif »: unticked, a same passage may serve two
 * objectives (CR 20, the rule of before). It is read by default FALSE, so a
 * version written before it keeps that rule, and it is set by a new version like
 * the consigne. The review freezes the one of the version that gave its grid.
 */
export const protocolRangementSchema = z.object({
  targetObjectiveIds: z
    .array(protocolLocalIdSchema)
    .max(PROTOCOL_STEPS_MAX * PROTOCOL_OBJECTIVES_MAX)
    .default([]),
  consigne: z.string().trim().max(PROTOCOL_RANGEMENT_CONSIGNE_MAX).default(''),
  oneObjectivePerPassage: z.boolean().default(false),
});
export type ProtocolRangement = z.infer<typeof protocolRangementSchema>;

/** A protocol with no rangement: no target, no consigne. */
export const PROTOCOL_RANGEMENT_EMPTY: ProtocolRangement = {
  targetObjectiveIds: [],
  consigne: '',
  oneObjectivePerPassage: false,
};

/**
 * The « Analyste » section of a protocol version (analyste.md §3, issue #196):
 * everything the analyst of the noyau reads of the protocol, versioned and
 * frozen with it. The LEVEL of each objective is not here - it is on the
 * objective (`analystLevel`) - but the rest is:
 *
 * - `pairThreshold` - a level with two readable objectives or more is fixed when
 *   EACH gives at least this many points to the same function (70);
 * - `soloThreshold` - a level with one readable objective is fixed when it gives
 *   at least this many points to a function (85);
 * - `styleWeight` - the share of the style in the note of a candidate, the rest
 *   being its compatibility (50);
 * - `styleRuns` - how many times the second pass is read, from 1 to 5, 1 by
 *   default (issue #229, §3.2, §7.6): the style of a candidate is the mean of its
 *   readings. A version stored before #229 reads at 1;
 * - `functionsReading` and `styleReading` - one free text per pass, the body of
 *   « Comment lire la matière » of the first and of « Comment lire le discours »
 *   of the second, whose headings, framing and contract stay in code. Four
 *   thousand characters each, an objective cited by `@` rendered under its NAME
 *   (`resolveAnalystReading`).
 *
 * `.default()` at both levels and no migration, the régime of `rangement`: a
 * version stored before #196 reads without a section, with the defaults above
 * and two empty readings - which makes it incomplete (§3.6), never unreadable.
 * The reading of before #196 (`analystReading`) stays in the content beside it,
 * neither served nor edited (K14).
 */
export const protocolAnalystSchema = z.object({
  pairThreshold: analystThresholdSchema.default(ANALYST_PAIR_THRESHOLD_DEFAULT),
  soloThreshold: analystThresholdSchema.default(ANALYST_SOLO_THRESHOLD_DEFAULT),
  styleWeight: analystStyleWeightSchema.default(ANALYST_STYLE_WEIGHT_DEFAULT),
  styleRuns: analystStyleRunsSchema.default(ANALYST_STYLE_RUNS_DEFAULT),
  functionsReading: z.string().trim().max(ANALYST_READING_MAX).default(''),
  styleReading: z.string().trim().max(ANALYST_READING_MAX).default(''),
});
export type ProtocolAnalyst = z.infer<typeof protocolAnalystSchema>;

/** The section nobody has touched: the four defaults and two empty readings. */
export const PROTOCOL_ANALYST_DEFAULT: ProtocolAnalyst = {
  pairThreshold: ANALYST_PAIR_THRESHOLD_DEFAULT,
  soloThreshold: ANALYST_SOLO_THRESHOLD_DEFAULT,
  styleWeight: ANALYST_STYLE_WEIGHT_DEFAULT,
  styleRuns: ANALYST_STYLE_RUNS_DEFAULT,
  functionsReading: '',
  styleReading: '',
};

/**
 * The object behind the content schema, kept apart because a refinement wraps
 * it: `.partial()` and `.shape` are the object's, and the draft schema and
 * the rename schema below are built from them.
 */
const protocolContentObject = z.object({
  name: z.string().trim().min(1).max(PROTOCOL_NAME_MAX),
  /** The short description of the cards and of a link's choice page (§7.1). */
  description: z.string().trim().min(1).max(PROTOCOL_DESCRIPTION_MAX),
  steps: z.array(protocolStepSchema).min(1).max(PROTOCOL_STEPS_MAX),
  /**
   * « Lecture pour l'analyste » (noyau.md §4.9, issue #154): how the analyst of
   * the noyau reads the matter of THIS protocol - the body of the section
   * « Comment lire la matière » of its prompt, whose heading, grid and contract
   * stay in code (`analystSystemParts`).
   *
   * One text for the whole protocol (P4): it names the objectives by their
   * name, the one the analyst receives their extracts under, and says what runs
   * across them. Multiline text bounded like a direction (N31), versioned with
   * the protocol and frozen with it at the start of a session. Since #158 it
   * cites an objective with the token of the directions, `{{objectif:id}}`,
   * which renders the objective's NAME and never its matter
   * (`resolveAnalystReading`, N33 overturned).
   *
   * EMPTY by default at the read, and with no migration: a version stored
   * before #154 reads with no reading, and an empty one - spaces included, N35 -
   * makes an experience that plays the noyau sequence with it unable to start
   * (§3.7). A protocol CREATED from now on is born with the generic reading,
   * `ANALYST_READING_TEMPLATE`, put there by whoever creates it (N34): the
   * write schema fills nothing.
   */
  analystReading: z.string().trim().max(PROTOCOL_DIRECTION_MAX).default(''),
  /**
   * « Rangement » (scenario-succes.md §4, issue #177): the objectives the
   * rangeur files the conversation onto, and how. Empty by default at the
   * read, with no migration - see `protocolRangementSchema`.
   *
   * The reading of before #196, `analystReading` above, is KEPT in the content
   * and read as it always was (K14): `compat:prod` reads every version in
   * production with the new code, and would refuse the tag of a code that
   * dropped it. It is no longer served nor edited - the two readings of the
   * section below took its place.
   */
  rangement: protocolRangementSchema.default(PROTOCOL_RANGEMENT_EMPTY),
  /**
   * « Analyste » (analyste.md §3, issue #196): the thresholds, the weight of the
   * style and one reading per pass. Defaulted at the read, with no migration -
   * see `protocolAnalystSchema`.
   */
  analyst: protocolAnalystSchema.default(PROTOCOL_ANALYST_DEFAULT),
});

/**
 * The CONTENT of one version - the totality of the protocol at that version,
 * name included (§4.1).
 *
 * **Deliberately not `.strict()`** (§4.1): this schema parses rows written by
 * earlier versions of the code, and a strict object refuses a row the day a
 * field is added. Every nested shape follows the same rule.
 */
export const protocolContentSchema = protocolContentObject.superRefine(refuseDuplicateIds);
export type ProtocolContent = z.infer<typeof protocolContentSchema>;

/**
 * An objective as a DRAFT holds it: the same fields, the name allowed empty.
 *
 * A row the operator has just added has no name yet, and the autosave fires
 * while they type it. `min(1)` on the version's schema is what makes an empty
 * name a refusal of the VERSION (through `normalized`), never a 400 on the
 * draft.
 */
export const protocolObjectiveDraftSchema = protocolObjectiveSchema.extend({
  name: z.string().trim().max(PROTOCOL_OBJECTIVE_NAME_MAX).default(''),
});
export type ProtocolObjectiveDraft = z.infer<typeof protocolObjectiveDraftSchema>;

/** A step as a DRAFT holds it: the name allowed empty, the objectives as drafts. */
export const protocolStepDraftSchema = protocolStepSchema.extend({
  name: z.string().trim().max(PROTOCOL_STEP_NAME_MAX).default(''),
  objectives: z.array(protocolObjectiveDraftSchema).max(PROTOCOL_OBJECTIVES_MAX).default([]),
});
export type ProtocolStepDraft = z.infer<typeof protocolStepDraftSchema>;

/**
 * The content of a DRAFT: the same fields, each optional, the steps as drafts.
 *
 * It is what the editor SENDS and what it READS BACK: one schema for both
 * halves of the round trip. The bounds still apply to what IS there, and so
 * does the shape of a condition - a half-typed protocol is still a protocol.
 * Only the uniqueness refinement is left out: a draft is parsed on every
 * reload, and a duplicate id is a defect the version save refuses anyway.
 */
export const protocolDraftContentSchema = z.object({
  name: protocolContentObject.shape.name.optional(),
  description: protocolContentObject.shape.description.optional(),
  steps: z.array(protocolStepDraftSchema).max(PROTOCOL_STEPS_MAX).optional(),
  analystReading: protocolContentObject.shape.analystReading.optional(),
  rangement: protocolContentObject.shape.rangement.optional(),
  analyst: protocolContentObject.shape.analyst.optional(),
});
export type ProtocolDraftContent = z.infer<typeof protocolDraftContentSchema>;

/**
 * What « Passer en production » refuses (§4, strict resolution; §7.1): a
 * criterion cited at `latest` that has no production version, a criterion that
 * has been retired (it resolves to nothing, pinned or not), or a criterion id
 * the base does not hold at all.
 *
 * `objectiveName` is null when the citation is a `{{critere:id}}` variable in
 * a direction rather than an objective's criterion.
 */
const blockerPlace = {
  criterionId: z.string().min(1),
  criterionName: z.string(),
  stepId: protocolLocalIdSchema,
  stepIndex: z.number().int().nonnegative(),
  stepName: z.string(),
  objectiveName: z.string().nullable(),
} as const;

/**
 * The FOURTH blocker, since the grill of 2026-09-16 (§7.2, issue #81), minus
 * its `kind`: an objective posing a threshold on a criterion the CITED version
 * does not score.
 *
 * The threshold would be ignored at play time (H47, the garde au gel), so the
 * protocol would run - it just would not run as its author wrote it, and
 * « Passer en production » is where that is caught. It carries its pin, because
 * the answer differs by citation: a criterion scored in its latest version and
 * not in the one a citation pins is a pin to move, not a threshold to remove.
 *
 * Declared APART because the protocol's CARD carries the same entries as its
 * second yellow line (§7.1, §15). One shape and one walk: the card reads
 * `productionBlockers` and keeps this kind, so a library and a refusal cannot
 * name different faults for one citation.
 */
export const protocolUnscoredThresholdSchema = z
  .object({
    ...blockerPlace,
    pin: versionPinSchema,
    threshold: criterionThresholdSchema,
  })
  .strict();
export type ProtocolUnscoredThreshold = z.infer<typeof protocolUnscoredThresholdSchema>;
/* ---------------------------------------------------------------- the cards */

/** One step as the CARD names it: the mini-chain and its tooltip (§7.1, §15). */
export const protocolCardStepSchema = z
  .object({
    id: protocolLocalIdSchema,
    name: z.string().min(1),
  })
  .strict();
export type ProtocolCardStep = z.infer<typeof protocolCardStepSchema>;

/**
 * A criterion the DEVELOPMENT version cites and that has been retired (§4):
 * what the card's warning line names - « remplacer le critère X, retiré le
 * ..., cité par l'objectif Y ». Computed server side against the criteria
 * table, because only the server holds both halves of the comparison.
 */
export const protocolRetiredCriterionSchema = z
  .object({
    criterionId: z.string().min(1),
    criterionName: z.string().min(1),
    retiredAt: z.string().datetime(),
    stepIndex: z.number().int().nonnegative(),
    stepName: z.string().min(1),
    /** Null when the criterion is cited by a direction variable and not by an objective. */
    objectiveName: z.string().min(1).nullable(),
  })
  .strict();
export type ProtocolRetiredCriterion = z.infer<typeof protocolRetiredCriterionSchema>;

/*
 * The card's second yellow line - « seuil sans notation : critère X, objectif
 * Y » - is `protocolUnscoredThresholdSchema`, and it is declared with the
 * production blockers further down rather than here: it IS one of them, minus
 * its `kind`, because the card and « Passer en production » report the same
 * defect one screen apart and read the same walk to find it.
 */

/**
 * One card of the list (§4, §7.1, §15): the thirteen common fields of
 * `versionedCardShape`, plus what a protocol's card draws - its description,
 * the mini-chain of its steps, the number of jumps, the retired criteria it
 * still cites, and the thresholds it poses on criteria nobody scores. All read
 * off the DEVELOPMENT version.
 */
export const protocolCardSchema = z
  .object({
    ...versionedCardShape,
    description: z.string(),
    steps: z.array(protocolCardStepSchema),
    jumpCount: z.number().int().nonnegative(),
    retiredCriteria: z.array(protocolRetiredCriterionSchema),
    unscoredThresholds: z.array(protocolUnscoredThresholdSchema),
  })
  .strict();
export type ProtocolCard = z.infer<typeof protocolCardSchema>;

/** The list, served WHOLE - retired entries included - and filtered in the page (§4). */
export const protocolListSchema = z.object({ protocols: z.array(protocolCardSchema) }).strict();
export type ProtocolList = z.infer<typeof protocolListSchema>;

/** Everything the editor reads in one call. */
export const protocolDetailSchema = versionedDetailSchema(
  protocolContentSchema,
  protocolDraftContentSchema,
);
export type ProtocolDetail = z.infer<typeof protocolDetailSchema>;

/** One version read in full, for « Voir » and « Repartir de cette version ». */
export const protocolVersionSchema = versionedContentSchema(protocolContentSchema);
export type ProtocolVersion = z.infer<typeof protocolVersionSchema>;

/* ------------------------------------------ what a CLIENT is allowed to send */

/**
 * The content and the draft content again, with the ONE leniency of H46 taken
 * back out: a threshold a client SENDS is a minimum or nothing, never the
 * `{ op, value }` of the grill of 2026-09-09.
 *
 * Reading an old row and accepting a new one are two different questions, and
 * `protocolContentSchema` answers only the first. Left to answer both, a client
 * still posting the old object would be silently converted, and the rows H46
 * exists to read would keep being written - a second generation of them, with
 * nothing in the history to say when it stopped. So the old shape is a 400 here
 * while it stays readable there.
 *
 * Each of these is EXTENDED from its lenient twin rather than written out
 * again: a field added to a step, an objective or a citation appears on both
 * sides by construction, and the two cannot drift into two protocols.
 */
const strictCriterionRequirementSchema = criterionRequirementSchema.extend({
  threshold: criterionThresholdSchema.nullable().default(null),
});

const strictObjectiveSchema = protocolObjectiveSchema.extend({
  criteria: z
    .array(strictCriterionRequirementSchema)
    .max(PROTOCOL_OBJECTIVE_CRITERIA_MAX)
    .default([]),
});

const strictStepSchema = protocolStepSchema.extend({
  objectives: z.array(strictObjectiveSchema).max(PROTOCOL_OBJECTIVES_MAX).default([]),
});

/**
 * The content as a client is allowed to WRITE it - `protocolContentSchema`
 * without the leniency of H46.
 *
 * Exported because the HTTP routes are not the only write boundary: the MCP
 * assistant of §20 posts contents too (issue #76), and it was handed the
 * LENIENT schema, so the one client that composes a protocol from scratch was
 * also the one allowed to write the shape H46 exists to read. A boundary that
 * refuses on one door and converts on another is not a boundary.
 */
export const protocolContentWriteSchema = protocolContentObject
  .extend({ steps: z.array(strictStepSchema).min(1).max(PROTOCOL_STEPS_MAX) })
  .superRefine(refuseDuplicateIds)
  /*
    A rangement target the steps no longer carry leaves the list at the save,
    without a refusal (scenario-succes.md §4): an objective deleted from the
    version is not a mistake of the operator. The editor runs the same function
    on what it sends, so the box and the server never name two lists.
  */
  .transform((content) => ({
    ...content,
    rangement: rangementTargetsKept(content.steps, content.rangement),
  }));
export type ProtocolContentWrite = z.infer<typeof protocolContentWriteSchema>;

const strictObjectiveDraftSchema = protocolObjectiveDraftSchema.extend({
  criteria: z
    .array(strictCriterionRequirementSchema)
    .max(PROTOCOL_OBJECTIVE_CRITERIA_MAX)
    .default([]),
});

const strictStepDraftSchema = protocolStepDraftSchema.extend({
  objectives: z.array(strictObjectiveDraftSchema).max(PROTOCOL_OBJECTIVES_MAX).default([]),
});

const strictDraftContentSchema = protocolDraftContentSchema.extend({
  steps: z.array(strictStepDraftSchema).max(PROTOCOL_STEPS_MAX).optional(),
});

/** « Enregistrer une nouvelle version », with its optional note and its production checkbox. */
export const protocolSaveVersionSchema = saveVersionRequestSchema(protocolContentWriteSchema);
export type ProtocolSaveVersion = z.infer<typeof protocolSaveVersionSchema>;

/** An autosave of the draft, on the partial content of `protocolDraftContentSchema`. */
export const protocolSaveDraftSchema = saveDraftRequestSchema(strictDraftContentSchema);
export type ProtocolSaveDraft = z.infer<typeof protocolSaveDraftSchema>;

/** Renaming, on the protocol's OWN name field - see `renameRequestSchemaFor`. */
export const protocolRenameSchema = renameRequestSchemaFor(protocolContentObject.shape.name);
export type ProtocolRename = z.infer<typeof protocolRenameSchema>;

/* -------------------------------------------------------------- the filters */

/**
 * The four common sorts of §4, and no more: a protocol adds no filter and no
 * sort of its own (§15).
 */
export const protocolSortSchema = versionedSortSchema;
export type ProtocolSort = z.infer<typeof protocolSortSchema>;

export const PROTOCOL_DEFAULT_SORT: ProtocolSort = VERSIONED_DEFAULT_SORT;

/** The thirteen common filters of §4 plus the sort, as ONE object. */
export const protocolFiltersSchema = z
  .object({
    ...versionedFiltersShape,
    sort: protocolSortSchema.default(PROTOCOL_DEFAULT_SORT),
  })
  .strict();
export type ProtocolFilters = z.infer<typeof protocolFiltersSchema>;

/** The neutral state: every protocol, retired excluded, sorted by name. */
export const PROTOCOL_FILTERS_DEFAULT: ProtocolFilters = protocolFiltersSchema.parse({});

/* ------------------------------------------------------------- the refusals */

/**
 * What « Enregistrer une nouvelle version » refuses in a protocol (§7.1), one
 * entry per problem, each one NAMING its step so the screen can link to it.
 *
 * The first three are the letter of §7.1. The two `*-missing-objective` ones
 * are the same defect met by a trigger or an action rather than by a jump
 * (H22, §16): an objective removed while a condition still listens to it.
 *
 * `stepIndex` is ZERO-BASED; the screen adds one, like the frise does.
 */
const problemStep = {
  stepId: protocolLocalIdSchema,
  stepIndex: z.number().int().nonnegative(),
  stepName: z.string(),
} as const;

export const protocolProblemSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('step-without-cap'), ...problemStep }).strict(),
  /**
   * An « l'un de » group of a single citation (issue #181, H2), or a label
   * used again on a line that is not its neighbour: the later run is no group of
   * its own. « Un groupe "l'un de" demande au moins deux critères. »
   */
  z
    .object({
      kind: z.literal('or-group-too-small'),
      ...problemStep,
      objectiveId: protocolLocalIdSchema,
      objectiveName: z.string(),
      orGroup: z.string(),
    })
    .strict(),
  /**
   * A farewell step carrying objectives or exit conditions (issue #163, H77):
   * the editor never lets one be written, a content posed by hand or by the
   * assistant could. Refused rather than ignored at play time - an objective
   * nobody will ever judge is a protocol that does not do what it says.
   */
  z
    .object({ kind: z.literal('farewell-step-not-empty'), ...problemStep })
    .strict(),
  /**
   * A farewell step that also rereads on entry (issue #165, H93): it has no
   * objective to fill, so the reread would read for nothing. The editor
   * unticks the reread when the farewell is ticked; a content posed by hand or
   * by the assistant could still carry both.
   */
  z
    .object({ kind: z.literal('reread-on-farewell-step'), ...problemStep })
    .strict(),
  /**
   * A farewell step that also hides its objectives from the interviewer
   * (issue #191, H19): it has none to hide. The editor unticks the case when
   * the farewell is ticked; a content posed by hand or by the assistant could
   * still carry both.
   */
  z
    .object({ kind: z.literal('hide-objectives-on-farewell-step'), ...problemStep })
    .strict(),
  z
    .object({
      kind: z.literal('objective-without-criterion'),
      ...problemStep,
      objectiveId: protocolLocalIdSchema,
      objectiveName: z.string(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('jump-to-missing-step'),
      ...problemStep,
      conditionId: protocolLocalIdSchema,
      conditionIndex: z.number().int().nonnegative(),
    })
    .strict(),
  z.object({ kind: z.literal('fallback-to-missing-step'), ...problemStep }).strict(),
  z
    .object({
      kind: z.literal('trigger-on-missing-objective'),
      ...problemStep,
      conditionId: protocolLocalIdSchema,
      conditionIndex: z.number().int().nonnegative(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('action-on-missing-objective'),
      ...problemStep,
      conditionId: protocolLocalIdSchema,
      conditionIndex: z.number().int().nonnegative(),
    })
    .strict(),
  /**
   * The two refusals of the « Rangement » section (scenario-succes.md §4, H6,
   * issue #177), the only members that name NO step: the section belongs to the
   * protocol as a whole. Objectives ticked as targets and an empty consigne,
   * then a consigne and no target - `rangementProblems`.
   */
  z
    .object({ kind: z.literal('rangement-consigne-missing') })
    .strict(),
  z.object({ kind: z.literal('rangement-targets-missing') }).strict(),
]);
export type ProtocolProblem = z.infer<typeof protocolProblemSchema>;

/** The two problems of the « Rangement » section, which name no step. */
export type ProtocolRangementProblem = Extract<
  ProtocolProblem,
  { kind: 'rangement-consigne-missing' | 'rangement-targets-missing' }
>;

/** Every other problem: each one names its step (`stepId`, `stepIndex`, `stepName`). */
export type ProtocolStepProblem = Exclude<ProtocolProblem, ProtocolRangementProblem>;

export const protocolProductionBlockerSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('criterion-without-production'), ...blockerPlace }).strict(),
  z
    .object({
      kind: z.literal('criterion-retired'),
      ...blockerPlace,
      retiredAt: z.string().datetime(),
    })
    .strict(),
  z.object({ kind: z.literal('criterion-unknown'), ...blockerPlace }).strict(),
  protocolUnscoredThresholdSchema.extend({ kind: z.literal('threshold-without-scoring') }),
]);
export type ProtocolProductionBlocker = z.infer<typeof protocolProductionBlockerSchema>;

/**
 * The two refusals that belong to the protocol and to nothing else, as one
 * closed catalogue - the same doctrine as `versionedErrorCodeSchema`, and
 * deliberately NOT the same enum: these are states of the CONTENT, not of the
 * base, and a shared enum would offer eight codes where two exist.
 */
export const protocolErrorCodeSchema = z.enum(['invalid-steps', 'production-unresolved']);
export type ProtocolErrorCode = z.infer<typeof protocolErrorCodeSchema>;

/**
 * The body of a protocol refusal: the code, and the LIST behind it - problems
 * for `invalid-steps`, blockers for `production-unresolved`. Both arrays are
 * always present so a reader never branches on an absent key.
 */
export const protocolErrorSchema = z
  .object({
    error: z.string().min(1),
    code: protocolErrorCodeSchema,
    problems: z.array(protocolProblemSchema).default([]),
    blockers: z.array(protocolProductionBlockerSchema).default([]),
  })
  .strict();
export type ProtocolError = z.infer<typeof protocolErrorSchema>;
